// addPrimaryKey — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'add-primary-key', primaryKey })` op (DSE2 §3.B1).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice B1.
//
// Per-engine support (mirrors DSE2's `synthesizeAddPrimaryKey` rules):
//   - sqlite: rejected on ALTER (recreate path opt-in lives in Slice H3).
//   - mssql: optional `clustered: 'clustered' | 'nonclustered'`.
//   - postgres: optional `includeColumns` (11+); optional `deferrable`.
//   - oracle: optional `tablespace`; optional `deferrable`.
//   - mysql: columns + name only; no per-engine extras (AUTO_INCREMENT-on-PK
//     is a column-level concern surfaced as an info note in the form).

import type { DdlEdit, PrimaryKeyDef } from '../../../../sql-resolver/types/ddlEdit';
import type { SqlEngine } from '../../../../sql-grammar/model';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export type AddPrimaryKeyArgs = {
  readonly primaryKey: PrimaryKeyDef;
};

export function buildAddPrimaryKeyEdit(args: AddPrimaryKeyArgs): DdlEdit {
  return { op: 'add-primary-key', primaryKey: args.primaryKey };
}

export async function commitAddPrimaryKey(
  ctx: VqeDdlEditContext,
  args: AddPrimaryKeyArgs,
): Promise<void> {
  const edit = buildAddPrimaryKeyEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

export function isAddPrimaryKeySupported(engine: SqlEngine): boolean {
  return engine !== 'sqlite';
}

export function getAddPrimaryKeyUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'sqlite') {
    return (
      'SQLite does not support ALTER TABLE … ADD CONSTRAINT PRIMARY KEY. '
      + 'Declare the primary key inline at CREATE TABLE time, or recreate '
      + 'the table with the constraint — use “Edit as SQL” to write the change.'
    );
  }
  return '';
}

export function isClusteredSupported(engine: SqlEngine): boolean {
  return engine === 'mssql';
}

export function isIncludeColumnsSupported(engine: SqlEngine): boolean {
  return engine === 'postgres';
}

export function isTablespaceSupported(engine: SqlEngine): boolean {
  return engine === 'oracle';
}

export function isDeferrableSupported(engine: SqlEngine): boolean {
  return engine === 'postgres' || engine === 'oracle';
}

export function isAutoIncrementOnPrimaryKey(engine: SqlEngine): boolean {
  return engine === 'mysql';
}

export function getEngineFieldUnsupportedTooltip(
  engine: SqlEngine,
  field: 'clustered' | 'includeColumns' | 'tablespace' | 'deferrable',
): string {
  switch (field) {
    case 'clustered':
      return 'CLUSTERED / NONCLUSTERED is a SQL Server-only PRIMARY KEY modifier.';
    case 'includeColumns':
      return 'INCLUDE columns require PostgreSQL 11+ and are unsupported on other engines.';
    case 'tablespace':
      return 'USING INDEX TABLESPACE is an Oracle-only PRIMARY KEY modifier.';
    case 'deferrable':
      return 'DEFERRABLE INITIALLY is supported on PostgreSQL + Oracle only.';
  }
  void engine;
  return '';
}

/** Pre-flight validator mirroring DSE2's `validatePk` rules so the form can
 *  surface engine errors before the synthesizer would throw. Returns null
 *  when the spec is acceptable. */
export function validatePrimaryKeyForEngine(
  engine: SqlEngine,
  pk: PrimaryKeyDef,
): string | null {
  if (pk.columns.length === 0) {
    return 'PRIMARY KEY requires at least one column.';
  }
  if (engine !== 'mssql' && pk.clustered !== undefined) {
    return getEngineFieldUnsupportedTooltip(engine, 'clustered');
  }
  if (engine !== 'postgres' && pk.includeColumns !== undefined && pk.includeColumns.length > 0) {
    return getEngineFieldUnsupportedTooltip(engine, 'includeColumns');
  }
  if (engine !== 'oracle' && pk.tablespace !== undefined && pk.tablespace !== '') {
    return getEngineFieldUnsupportedTooltip(engine, 'tablespace');
  }
  if (engine !== 'oracle' && engine !== 'postgres' && pk.deferrable !== undefined) {
    return getEngineFieldUnsupportedTooltip(engine, 'deferrable');
  }
  return null;
}

export function parseColumnList(input: string): readonly string[] {
  return input
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}
